import { DocumentRowContextMenu } from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { FolderTableRow } from "@/domains/documents/components/organisms/FolderTableRow";
import type { TreeFolder } from "@/domains/documents/types";
import type { VirtualItem } from "@tanstack/react-virtual";
import { observer } from "mobx-react-lite";
import type { ComponentProps } from "react";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import type { FlattenedItem } from "./useTableRows";
type FolderContentsProps = DocumentTablePresentation & {
  folder: TreeFolder;
  folderUnifiedIndex: number | undefined;
};
export const TableFolderContents = observer(function TableFolderContents(
  props: FolderContentsProps
) {
  const { folder, folderMoveProps, deleteLabel } = props;
  return (
    <FolderTableRow
      folder={folder}
      {...folderMoveProps(folder)}
      {...folderRowSelection(props)}
      {...folderRowColumns(props)}
      {...folderRowVersion(props)}
      deleteLabel={deleteLabel}
      {...folderRowRename(props)}
      {...folderRowActions(props)}
    />
  );
});

export const TableFolderRow = observer(function TableFolderRow(
  props: DocumentTablePresentation & { item: FlattenedItem; virtualRow: VirtualItem }
) {
  const {
    item,
    unifiedIndexById,
    virtualRow,
    folderRename,
    buildFolderMenuConfig,
    measureVirtualRow,
  } = props;
  if (item.kind !== "folder") return null;

  const folder = item.folder;
  // Same id lookup the document rows use: a folder in search
  // mode sits below any expanded version rows, whose
  // virtualizer slots are not `unifiedItems` entries.
  const folderUnifiedIndex = unifiedIndexById.get(`folder:${folder.id}`);
  return (
    <DocumentRowContextMenu
      key={`${virtualRow.key}-folder-${folder.id}`}
      variant="folder"
      disabled={folderRename.isEditing(folder.id)}
      {...buildFolderMenuConfig(folder)}
    >
      <div
        data-index={virtualRow.index}
        ref={measureVirtualRow}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        <TableFolderContents {...props} folder={folder} folderUnifiedIndex={folderUnifiedIndex} />
      </div>
    </DocumentRowContextMenu>
  );
});

function folderRowColumns(
  props: FolderContentsProps
): Pick<
  ComponentProps<typeof FolderTableRow>,
  | "showCheckbox"
  | "showVersion"
  | "showDateCreated"
  | "showStatus"
  | "showUser"
  | "showTags"
  | "showParent"
  | "showActions"
> {
  const {
    showCheckbox,
    showVersion,
    showDateCreated,
    showStatus,
    showUser,
    showTags,
    showParent,
    showActions,
  } = props;
  return {
    showCheckbox: showCheckbox,

    showVersion: showVersion,

    showDateCreated: showDateCreated,

    showStatus: showStatus,

    showUser: showUser,

    showTags: showTags,

    showParent: showParent,

    showActions: showActions,
  };
}

function folderRowVersion(
  props: FolderContentsProps
): Pick<ComponentProps<typeof FolderTableRow>, "version"> {
  const { versionedFolder, folder, folderVersionsOpen, setFolderVersionsOpen } = props;
  return {
    version:
      versionedFolder?.folderId === folder.id && versionedFolder.versions.length > 0
        ? {
            latestVersionNo: versionedFolder.versions[0].versionNo,
            isExpanded: folderVersionsOpen,
            onToggle: () => setFolderVersionsOpen((open) => !open),
            ariaLabel: versionedFolder.toggleLabel,
          }
        : undefined,
  };
}

function folderRowSelection(
  props: FolderContentsProps
): Pick<
  ComponentProps<typeof FolderTableRow>,
  "isSelected" | "onClick" | "onSelect" | "onRowClickCapture"
> {
  const {
    selectedFolderIds,
    folder,
    store,
    onFolderSelect,
    setAnchorIndex,
    onFolderClick,
    folderUnifiedIndex,
    handleRowClickCapture,
  } = props;
  return {
    isSelected: selectedFolderIds.includes(folder.id),

    onClick: () => {
      store?.clearSelection();
      selectedFolderIds.forEach((id) => onFolderSelect?.(id, false));
      setAnchorIndex(null);
      onFolderClick?.(folder.id);
    },

    onSelect: onFolderSelect ? (isSelected) => onFolderSelect(folder.id, isSelected) : undefined,

    onRowClickCapture:
      folderUnifiedIndex === undefined
        ? undefined
        : handleRowClickCapture(folder.id, folderUnifiedIndex),
  };
}

function folderRowRename(
  props: FolderContentsProps
): Pick<
  ComponentProps<typeof FolderTableRow>,
  "isEditing" | "editName" | "onRename" | "onNameChange" | "onNameConfirm" | "onNameCancel"
> {
  const { folderRename, folder, buildFolderMenuConfig } = props;
  return {
    isEditing: folderRename.isEditing(folder.id),

    editName: folderRename.editName,

    onRename: buildFolderMenuConfig(folder).onRename,

    onNameChange: folderRename.setEditName,

    onNameConfirm: () => folderRename.confirmRename(folder.id),

    onNameCancel: folderRename.cancelRename,
  };
}

function folderRowActions(
  props: FolderContentsProps
): Pick<
  ComponentProps<typeof FolderTableRow>,
  "onDelete" | "onCreateSubfolder" | "onExport" | "onOrganize" | "onShare"
> {
  const { buildFolderMenuConfig, folder, onCreateSubfolder, onExportFolder, onShareFolder } = props;
  return {
    onDelete: buildFolderMenuConfig(folder).onDelete,

    onCreateSubfolder: onCreateSubfolder ? () => onCreateSubfolder(folder.id) : undefined,

    onExport: onExportFolder ? () => onExportFolder(folder.id) : undefined,

    onOrganize: buildFolderMenuConfig(folder).onOrganize,

    onShare: onShareFolder ? () => onShareFolder(folder.id) : undefined,
  };
}
